import { type RefObject } from 'react';
import { Trans, useTranslation } from 'next-i18next/pages';
import { CountryCode, LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP, MarketCountryCode } from '@pkgs/api';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useConsentLabels } from '@/hooks/useConsentLabels';
import { ExpandableLabel } from '@/components/property-expose/ExpandableLabel/ExpandableLabel';

import styles from '../ContactForm.module.css';

export const ConsentLabel = ({
  showsPrivacyStatement,
  countryAlpha2,
  consentRef,
}: {
  showsPrivacyStatement: boolean;
  consentRef?: RefObject<HTMLDivElement>;
  countryAlpha2?: CountryCode;
}) => {
  const { countryCode, language } = useSettings();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  const transformedCountryCode =
    countryCode === MarketCountryCode['private-office'] ? LANGUAGE_ONLY_COUNTRYCODE_LANGUAGE_MAP[language].countryCode : countryCode;

  const { entityName, ImprintButtonComponent, PrivacyLinkComponent, ImprintModalComponent } = useConsentLabels();
  const officeName = entityName ?? 'Engel & Völkers';
  const isFrenchListing = countryAlpha2 === CountryCode.FR;
  const consentTextKey = isFrenchListing ? 'search.contact.form.label.consentText.france' : 'search.contact.form.label.consentText';
  const consentInformationKey = isFrenchListing
    ? 'search.contact.form.label.consentInformation.france'
    : 'search.contact.form.label.consentInformation';

  return (
    <ExpandableLabel className={styles.expandableConsent} maxLines={3} data-testid="expandable-label_consent-label">
      <div ref={consentRef}>
        <Copy
          className={styles.consentCopy}
          size="m"
          text={
            <>
              <Trans
                i18nKey={consentTextKey}
                t={t}
                values={{ officeName }}
                components={
                  entityName
                    ? [
                        <ImprintButtonComponent key="imprint-button" />,
                        <a
                          className={styles.link}
                          data-testid="ev-group-link"
                          href={`/${transformedCountryCode.toLowerCase()}/${language}/shops`}
                          key="ev-group-link"
                          target="_blank"
                          rel="noreferrer"
                        />,
                      ]
                    : []
                }
              />
              {showsPrivacyStatement && (
                <>
                  {isFrenchListing ? <br /> : <span>{'\u00A0'}</span>}
                  <Trans
                    i18nKey={consentInformationKey}
                    values={{ officeName }}
                    components={[<span data-testid="office-name" key="office-name" />, <PrivacyLinkComponent key="privacy-link" />]}
                    t={t}
                  />
                </>
              )}
            </>
          }
        />
      </div>
      <ImprintModalComponent />
    </ExpandableLabel>
  );
};
